import React, { useState } from "react";
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { createPortal } from 'react-dom';

const IraProgressHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-ira-progress"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content">
          <div className="drawer-header">
            <div className="header-title">
              <span className="emoji">🟠</span>
              <span className="title">IRA – In Progress Tab Help</span>
            </div>
            <button className="close-button" onClick={toggle} aria-label="Close help panel">
              &times;
            </button>
          </div>

          <div className="drawer-body">
  <p>
    The In-progress Accident IRA Detail Page shows details of an accident where the investigation has already started. 
    This page helps the company track the progress of investigation, capture findings, and ensure structured analysis 
    leading to CAPA (Corrective and Preventive Actions).
  </p>

  <hr />

  <h5 className="section-title">📄 Record Overview</h5>
  <ul className="steps-list">
    <li>• Record Number – e.g., 940</li>
    <li>• IRA Type – UA / UC (Unsafe Act / Unsafe Condition)</li>
    <li>• Activity Name – e.g., Bar Bending</li>
    <li>• Reported Month – e.g., July</li>
  </ul>
  <p>Below this, the timeline displays:</p>
  <ul className="steps-list">
    <li>• Reported Details</li>
    <li>• Incident Date</li>
    <li>• Injured Person Details</li>
    <li>• Discussion Comments (if any)</li>
  </ul>

  <hr />

  <h5 className="section-title">📌 Sections of the Page</h5>
  <ol className="steps-list">
    <li><b>Reported Details</b>
      <ul>
        <li>• Evidence – images uploaded during accident reporting.</li>
        <li>• Incident Description – what happened during the accident.</li>
      </ul>
    </li>

    <li><b>Activity Information</b>
      <ul>
        <li>• Service and its linked Plant/Project/Client.</li>
        <li>• Department/Contractor/Location linked to the accident.</li>
      </ul>
    </li>

    <li><b>Incident Details</b>
      <ul>
        <li>• Incident Date – when the accident occurred.</li>
        <li>• Incident Time – exact time of occurrence.</li>
        <li>• Injured Person Details – who was injured and their role/identity.</li>
      </ul>
    </li>

    <li><b>Investigation Start Details</b>
      <ul>
        <li>• Shows when the investigation started.</li>
        <li>• Accident record shifts from Pending → In-progress once the team is assigned.</li>
      </ul>
    </li>

    <li><b>Investigation Target & Team Details</b>
      <ul>
        <li>• Displays the Investigation Target Date – by when investigation should be completed (up to CAPA recommendation).</li>
        <li>• Shows all team members with their names and roles.</li>
        <li>• Crown icon identifies the Team Lead.</li>
      </ul>
    </li>
  </ol>

  <hr />

  <h5 className="section-title">🧭 IRA Investigation Analysis – 5 Steps</h5>

  <p><b>Step 1 – Initial Response</b></p>
  <ul className="steps-list">
    <li>• Capture immediate actions taken after the accident.</li>
    <li>• Add actions in two ways:
      <ul>
        <li>1. Select from Dropdown (master data).</li>
        <li>2. Enter manually in Text Box.</li>
      </ul>
    </li>
    <li>• Click Add to save.</li>
    <li>• Upload documents (e.g., site photos, notes) if required.</li>
  </ul>

  <hr />

  <p><b>Step 2 – Initial Investigation</b></p>
  <ul className="steps-list">
    <li>• Select the injured person name and capture:</li>
    <li>• Identity Proof, Employment Type – Employee/Contractor, Gender, Age</li>
    <li>• Injury Type – e.g., Cut, Burn, Fracture</li>
    <li>• Working Shift, Injured Body Part, Time of Incident</li>
    <li>• Working Type (Permanent, Contract, etc.), Job Tenure, Joining Date</li>
    <li>• Create 5Y Analysis Questions – add question + response, link to injured person.</li>
    <li>• Search existing questions if needed.</li>
  </ul>

  <hr />

  <p><b>Step 3 – Data Gathering</b></p>
  <ul className="steps-list">
    <li>1. Witness Data – Add witness details, type, photo, and statement.</li>
    <li>2. Document Collection – Upload Paper/Digital, Physical Info, Position/Equipments, Sketch files.</li>
    <li>3. Mandays / Costing – Capture Mandays lost, cost, return-to-work type.</li>
    <li>4. 5M Analysis – Management, Machinery, Manpower, Material, Method.</li>
    <li>5. Job Analysis Questions – Capture responses, add notes, make mandatory if required.</li>
  </ul>

  <hr />

  <p><b>Step 4 – Root Cause Analysis</b></p>
  <ul className="steps-list">
    <li>1. Chronology of Events – timeline of events with date, time, source, description.</li>
    <li>2. Root Cause Methods:
      <ul>
        <li>• RCA – Contributing Factor, Immediate Factor, Root Causes (Fishbone Diagram).</li>
        <li>• CLC – select from predefined list; capture Causal Factor Type, Problem Category, Root Cause levels.</li>
      </ul>
    </li>
    <li>3. Accident Costing – capture cost heads (if enabled in admin settings).</li>
  </ul>

  <hr />

  <p><b>Step 5 – CAPA (Corrective & Preventive Actions)</b></p>
  <ul className="steps-list">
    <li>• Add CAPA with Category, Type (CA/PA), Action, Assigned User, Due Date, Priority.</li>
    <li>• Approval process – Team members propose, Team Lead approves → moves to Final CAPA List.</li>
    <li>• Freeze Investigation – Team Lead finalizes, moves record to Completed Tab.</li>
    <li>• Final CAPAs move to CAPA In-progress tab for implementation.</li>
    <li>• Assigned users notified via email.</li>
  </ul>

  <hr />

  <h5 className="section-title">💬 Discussion Section</h5>
  <p>A chat space for investigation team members – enables collaboration, sharing ideas, and preventing recurrence.</p>

  <hr />

  <h5 className="section-title">📊 Right-hand Side – UAUC Analysis</h5>
  <ul className="steps-list">
    <li>• IRA Analysis – accident statistics.</li>
    <li>• Near Miss Analysis.</li>
    <li>• UA vs UC Analysis.</li>
    <li>• Risk-Based Analysis.</li>
    <li>• Good Act & Toolbox Talk (TBT) count.</li>
    <li>• Internal & External Training count.</li>
    <li>• Employee & Contractor Training count.</li>
  </ul>

  <div className="alert alert-info">
    <b>✅ Once investigation is completed and “Freeze All” is done, the record automatically moves to Completed Accident IRA.</b>
  </div>
</div>

        </div>
      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default IraProgressHelpDrawer;
